import React, { useCallback, useEffect, useState } from "react";
import { api, formatApiError } from "@/api";
import { PageHeader } from "@/pages/admin/AdminDashboard";
import { Button } from "@/components/ui/button";
import { Tabs, TabsContent, TabsList, TabsTrigger } from "@/components/ui/tabs";
import { toast } from "sonner";
import { ChevronLeft, ChevronRight, Crown, MapPin, Network } from "lucide-react";
import { SiteStatsStrip } from "@/components/etablissement/SiteStatsStrip";
import { SiteHoursTable } from "@/components/etablissement/SiteHoursTable";
import { SiteCongesList } from "@/components/etablissement/SiteCongesList";
import { SiteFinanceList } from "@/components/etablissement/SiteFinanceList";
import { SiteSeasonPanel } from "@/components/etablissement/SiteSeasonPanel";
import { SitePlanningGrid } from "@/components/etablissement/SitePlanningGrid";
import { monthKey, monthLabel, shiftMonth, NETWORK_MODES } from "@/components/etablissement/shared";

const SIEGE_HINT = "Réservé au siège en mode réseau intégré. Contactez votre direction.";

export default function SalarieEtablissement() {
  const [month, setMonth] = useState(monthKey());
  const [data, setData] = useState(null);
  const [error, setError] = useState(null);

  const load = useCallback(async () => {
    try {
      const { data: d } = await api.get("/site/me", { params: { month } });
      setData(d); setError(null);
    } catch (e) { setError(formatApiError(e.response?.data?.detail) || "Accès refusé"); }
  }, [month]);
  useEffect(() => { load(); }, [load]);

  const run = async (fn, msg) => {
    try { await fn(); if (msg) toast.success(msg); await load(); }
    catch (e) { toast.error(formatApiError(e.response?.data?.detail)); throw e; }
  };

  if (error) {
    return (
      <div className="p-8 max-w-2xl" data-testid="site-manager-denied">
        <div className="border border-[#DEE2E6] p-8 bg-[#F8F9FA]">
          <div className="text-xs uppercase tracking-[0.3em] text-[#868E96] font-bold mb-3">// Mon établissement</div>
          <h1 className="font-display text-3xl font-black tracking-tighter mb-3">Accès réservé aux responsables</h1>
          <p className="text-[#495057] text-sm">{error}</p>
        </div>
      </div>
    );
  }
  if (!data) return <div className="p-10 text-sm text-[#868E96]">Chargement…</div>;

  const { site, perms, entreprise } = data;
  const mode = NETWORK_MODES[entreprise.network_mode] || NETWORK_MODES.multi;

  return (
    <div className="min-h-full" data-testid="site-manager-page">
      <PageHeader eyebrow="Mon établissement" title={site.name}
        sub={<span className="inline-flex items-center gap-3 flex-wrap"><span className="inline-flex items-center gap-1"><Crown className="h-3.5 w-3.5 text-[#FFD700]" /> Vous êtes responsable</span><span className="inline-flex items-center gap-1"><Network className="h-3.5 w-3.5" /> {mode.label}</span>{(site.ville || site.adresse) && <span className="inline-flex items-center gap-1"><MapPin className="h-3.5 w-3.5" /> {[site.adresse, site.ville].filter(Boolean).join(", ")}</span>}</span>}
        right={
          <div className="flex items-center gap-2">
            <Button variant="outline" onClick={() => setMonth(shiftMonth(month, -1))} data-testid="sm-month-prev" className="h-10 px-2"><ChevronLeft className="h-4 w-4" /></Button>
            <div className="h-10 px-4 border border-[#DEE2E6] bg-white flex items-center text-sm font-semibold min-w-[170px] justify-center" data-testid="sm-month-label">{monthLabel(month)}</div>
            <Button variant="outline" onClick={() => setMonth(shiftMonth(month, 1))} data-testid="sm-month-next" className="h-10 px-2"><ChevronRight className="h-4 w-4" /></Button>
          </div>
        } />

      <div className="p-6 lg:p-10 space-y-6">
        <SiteStatsStrip stats={data.stats} />
        <div className="text-xs text-[#495057] bg-[#F8F9FA] border border-[#DEE2E6] px-3 py-2" data-testid="sm-scope-hint">
          Accès limité aux données de <span className="font-semibold">{site.name}</span> ({site.code}). {mode.desc}
        </div>

        <Tabs defaultValue="equipe">
          <TabsList className="w-full justify-start flex-wrap h-auto bg-transparent border-b border-[#DEE2E6] rounded-none p-0 gap-1">
            {[["equipe", "Équipe"], ["planning", "Planning"], ["conges", "Congés"], ["finance", "Finance"], ["saison", "Saison"]].map(([k, l]) => (
              <TabsTrigger key={k} value={k} data-testid={`sm-tab-${k}`}
                className="rounded-none border-b-2 border-transparent data-[state=active]:border-[#002FA7] data-[state=active]:text-[#002FA7] data-[state=active]:shadow-none text-xs uppercase tracking-[0.15em] font-bold px-3 py-2">{l}</TabsTrigger>
            ))}
          </TabsList>

          <TabsContent value="equipe" className="pt-4">
            <SiteHoursTable salaries={data.salaries} responsableId={site.responsable_id} />
          </TabsContent>

          <TabsContent value="planning" className="pt-4">
            <SitePlanningGrid canEdit={perms.planning_edit} lockedHint={SIEGE_HINT}
              fetchWeek={async (from, to) => (await api.get("/site/planning", { params: { date_start: from, date_end: to } })).data}
              onSave={(form) => run(async () => { const { data: r } = await api.post("/site/planning", form); if (r.warning) toast.warning(r.warning); }, "Planning mis à jour")}
              onDelete={(id) => run(() => api.delete(`/site/planning/${id}`), "Créneau effacé")} />
          </TabsContent>

          <TabsContent value="conges" className="pt-4">
            <SiteCongesList conges={data.conges} canDecide={perms.conges_decide} lockedHint={`Validation des congés : ${SIEGE_HINT}`}
              onDecide={(id, status, commentaire) => run(() => api.post(`/site/conges/${id}/action`, { status, commentaire }), status === "approved" ? "Congé validé" : "Congé refusé")} />
          </TabsContent>

          <TabsContent value="finance" className="pt-4">
            <SiteFinanceList entries={data.finance} canEdit={perms.finance_edit} lockedHint={`Saisie du CA : ${SIEGE_HINT}`}
              onSave={(body) => run(() => api.post("/site/finance", body), "CA enregistré")}
              onDelete={(id) => run(() => api.delete(`/site/finance/${id}`), "Entrée supprimée")} />
          </TabsContent>

          <TabsContent value="saison" className="pt-4">
            <SiteSeasonPanel site={site} seasonStats={data.season_stats} seasonalMode={entreprise.seasonal_mode} canEdit={false} />
          </TabsContent>
        </Tabs>
      </div>
    </div>
  );
}
